import React, { useState, useMemo } from "react";
import {
  TextField,
  Checkbox,
  ListItemText,
  FormHelperText,
  Popper,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";
import { primaryBadgeSx } from "../styles/badgeSx";

type SelectOption = Record<string, any>;

type RpMuiSelectStyles = {
  root?: React.CSSProperties;
  checkbox?: React.CSSProperties;
  input?: React.CSSProperties;
  label?: React.CSSProperties;
  option?: React.CSSProperties;
};

type RpMuiSelectProps = {
  options?: SelectOption[];
  value?: any[];
  onChange?: (_value: any[]) => void;
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  styles?: RpMuiSelectStyles;
  labelKey?: string;
  valueKey?: string;
  limitTags?: number;
  size?: "small" | "medium";
  className?: string;
  selectAll?: boolean;
  renderOption?: (_props: any, _option: SelectOption) => React.ReactNode;
  startAdornment?: React.ReactNode;
  onInputChange?: (..._args: any[]) => void;
  [key: string]: any;
};

// Styled Popper component
const StyledPopper = styled(Popper)(() => ({
  zIndex: "9999 !important",
  width: "100%",
}));

// Styled Autocomplete component
const StyledAutocomplete = styled(Autocomplete as any)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "var(--neutral-white)",
    color: (theme.vars || theme).palette.text.primary,
    borderRadius: "var(--radius-control)",
    minHeight: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${(theme.vars || theme).palette.action.active}`,
    },
    // Badge delete icons keep their primary color (primaryBadgeSx).
    "& .MuiSvgIcon-root:not(.MuiChip-deleteIcon)": {
      color: (theme.vars || theme).palette.text.primary,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--neutral-600)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    },
    "&.Mui-disabled": {
      backgroundColor: (theme.vars || theme).palette.action.disabledBackground,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "& .MuiSvgIcon-root": {
        color: (theme.vars || theme).palette.action.disabled,
      },
      "& .MuiAutocomplete-tag": {
        backgroundColor: (theme.vars || theme).palette.action
          .disabledBackground,
        color: (theme.vars || theme).palette.action.disabled,
      },
      "& input::placeholder": {
        color: (theme.vars || theme).palette.action.disabled,
        opacity: 1,
      },
    },
    "& input::placeholder": {
      color: (theme.vars || theme).palette.text.secondary,
      opacity: 1,
    },
  },
  "& .MuiAutocomplete-endAdornment": {
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    position: "absolute",
    zIndex: 2,
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingRight: "48px !important",
    flexWrap: "nowrap",
    height: "100%",
    // The notched outline's top border sits half a pixel above this box.
    overflow: "clip",
    overflowClipMargin: "1px",
  },
  "& .MuiAutocomplete-input": {
    padding: "0 !important",
    width: "0 !important",
    minWidth: "30px",
  },
  "& .MuiAutocomplete-tag": {
    height: "24px",
    margin: "2px",
    display: "none",
    alignItems: "center",
    maxWidth: "calc(100% - 35px)",
    "&:first-of-type": {
      display: "inline-flex",
    },
    "& .MuiChip-label": {
      fontSize: "0.7rem",
      padding: "0 6px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontWeight: 600,
    },
    "& .MuiChip-deleteIcon": {
      fontSize: "16px",
      margin: "0 2px",
    },
  },
}));

const RpMuiSelect = ({
  options = [],
  value = [],
  onChange,
  multiple = true,
  label = "",
  placeholder = "Select Options",
  required = false,
  helperText = "",
  styles = {},
  labelKey = "label",
  valueKey = "value",
  limitTags = 1,
  size = "small",
  className = "",
  selectAll = true,
  renderOption: renderOptionProp,
  startAdornment,
  ...rest
}: RpMuiSelectProps) => {
  const { onInputChange: externalOnInputChange, ...autocompleteProps } = rest;
  const [touched, setTouched] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const isAllSelected =
    multiple &&
    options &&
    options.length > 0 &&
    value &&
    value.length === options.length;

  const hasError = touched && required && (!value || value.length === 0);

  const handleChange = (_event: React.SyntheticEvent, newValue: any) => {
    setTouched(true);

    if (!multiple) {
      if (!newValue) return onChange?.([]);
      return onChange?.([newValue[valueKey]]);
    }

    if (newValue && newValue.some((item) => item[valueKey] === "select-all")) {
      const newSelectedOptions = isAllSelected
        ? []
        : options.map((opt) => opt[valueKey]);
      return onChange?.(newSelectedOptions);
    }

    const newSelectedOptions = newValue
      ? newValue.map((item) => item[valueKey])
      : [];
    return onChange?.(newSelectedOptions);
  };

  const displayValue = useMemo(() => {
    if (!multiple) {
      if (!value || value.length === 0) return null;
      return options.find((opt) => opt[valueKey] === value[0]) || null;
    }
    if (!value || value.length === 0) return [];
    return options.filter((option) => value.includes(option[valueKey]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiple, valueKey, JSON.stringify(value), options]);

  const optionsList = useMemo(() => {
    if (!multiple) return options || [];
    return options && options.length > 0 && selectAll
      ? [{ [labelKey]: "Select All", [valueKey]: "select-all" }, ...options]
      : options || [];
  }, [multiple, options, selectAll, labelKey, valueKey]);

  const getPlaceholder = () => {
    if (multiple && value && value.length > 0) return "";
    if (!multiple && value && value[0]) return "";
    return placeholder;
  };

  return (
    <div style={styles.root}>
      <StyledAutocomplete
        limitTags={limitTags}
        multiple={multiple}
        size={size}
        freeSolo
        disableCloseOnSelect={multiple}
        className={className}
        {...autocompleteProps}
        options={optionsList}
        inputValue={inputValue}
        onInputChange={(event, nextInputValue, reason) => {
          if (
            reason === "input" &&
            shouldBlockUnsafeSearchInput(nextInputValue)
          ) {
            return;
          }

          setInputValue(nextInputValue);
          externalOnInputChange?.(event, nextInputValue, reason);
        }}
        getOptionLabel={(option: SelectOption) => {
          if (!option) return "";
          return option[labelKey] || "";
        }}
        isOptionEqualToValue={(option: SelectOption, val: SelectOption) => {
          if (!option || !val) return false;
          return option[valueKey] === val[valueKey];
        }}
        renderOption={(props: any, option: SelectOption) => {
          if (!option) return null;
          if (renderOptionProp) return renderOptionProp(props, option);

          // eslint-disable-next-line no-unused-vars
          const { key, ...liProps } = props;
          return multiple ? (
            <li
              key={key}
              {...liProps}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Checkbox
                checked={
                  option[valueKey] === "select-all"
                    ? isAllSelected
                    : value && value.includes(option[valueKey])
                }
                size="small"
                color="primary"
                style={styles.checkbox}
              />
              <ListItemText
                primary={option[labelKey]}
                slotProps={{
                  primary: {
                    style: {
                      fontSize: ".8rem",
                      ...styles?.option,
                    },
                  },
                }}
              />
            </li>
          ) : (
            <li key={key} {...liProps}>
              <ListItemText
                primary={option[labelKey]}
                slotProps={{
                  primary: {
                    style: {
                      fontSize: ".8rem",
                      ...styles?.option,
                    },
                  },
                }}
              />
            </li>
          );
        }}
        renderInput={(params: any) => (
          <TextField
            {...params}
            variant="outlined"
            label={label}
            placeholder={getPlaceholder()}
            style={{
              ...params.style,
              ...styles.input,
            }}
            required={required}
            error={hasError}
            onBlur={() => setTouched(true)}
            helperText={hasError ? helperText : ""}
            slotProps={{
              inputLabel: { style: styles.label },
              input: {
                ...params.InputProps,
                startAdornment: startAdornment ? (
                  <>
                    {startAdornment}
                    {params.InputProps.startAdornment}
                  </>
                ) : (
                  params.InputProps.startAdornment
                ),
                endAdornment: <>{params.InputProps.endAdornment}</>,
              },
            }}
          />
        )}
        value={displayValue}
        onChange={handleChange}
        slotProps={{
          popper: {
            component: StyledPopper,
            placement: "bottom-start",
          },
          chip: { sx: primaryBadgeSx },
        }}
      />
      {hasError && helperText && (
        <FormHelperText error>{helperText}</FormHelperText>
      )}
    </div>
  );
};

export default RpMuiSelect;
